/**
 * Safe composition handler utilities for Draft.js
 * Prevents getIn errors on undefined getBlockTree results
 */

/**
 * Creates a safe wrapper for Draft.js composition operations
 * @param {Function} originalHandler - The original composition handler
 * @returns {Function} - Safe wrapped handler
 */
export const createSafeCompositionHandler = (originalHandler) => {
    return (...args) => {
        try {
            // Extract editorState and other arguments
            const editorState = args[0];
            const blockKey = args[1];
            const decoratorKey = args[2];
            const leafKey = args[3];
            
            // Validate editorState
            if (!editorState || typeof editorState.getBlockTree !== 'function') {
                console.warn('SafeCompositionHandler: Invalid editorState');
                return editorState; // Return original state
            }
            
            // Validate blockKey
            if (!blockKey) {
                console.warn('SafeCompositionHandler: Invalid blockKey');
                return editorState; // Return original state
            }
            
            // Safely get block tree
            let blockTree;
            try {
                blockTree = editorState.getBlockTree(blockKey);
            } catch (error) {
                console.warn('SafeCompositionHandler: Error getting block tree:', error);
                return editorState; // Return original state
            }
            
            // Check if blockTree is valid
            if (!blockTree || typeof blockTree.getIn !== 'function') {
                console.warn('SafeCompositionHandler: Invalid blockTree for key:', blockKey);
                return editorState; // Return original state
            }
            
            // Safely call getIn
            let blockData;
            try {
                const path = [decoratorKey, 'leaves', leafKey];
                blockData = blockTree.getIn(path);
            } catch (error) {
                console.warn('SafeCompositionHandler: Error calling getIn:', error);
                return editorState; // Return original state
            }
            
            // If we get here, everything is valid, call original handler
            return originalHandler(...args);
            
        } catch (error) {
            console.error('SafeCompositionHandler: Unexpected error:', error);
            return args[0]; // Return original editorState
        }
    };
};

/**
 * Patches Draft.js composition handler to prevent getIn errors
 */
export const patchCompositionHandler = () => {
    try {
        // Check if Draft.js is available
        if (typeof window !== 'undefined' && window.Draft) {
            const { DraftEditorCompositionHandler } = window.Draft;
            
            if (DraftEditorCompositionHandler && typeof DraftEditorCompositionHandler === 'object') {
                // Patch the composition handler methods
                const methodsToPatch = [
                    'onCompositionStart',
                    'onCompositionEnd',
                    'onCompositionUpdate'
                ];
                
                methodsToPatch.forEach(methodName => {
                    const originalMethod = DraftEditorCompositionHandler[methodName];
                    
                    if (originalMethod && typeof originalMethod === 'function') {
                        DraftEditorCompositionHandler[methodName] = createSafeCompositionHandler(originalMethod);
                        console.log(`Draft.js ${methodName} patched successfully`);
                    }
                });
            }
        }
    } catch (error) {
        console.error('Error patching composition handler:', error);
    }
};

/**
 * Sets up safe composition handling
 */
export const setupSafeCompositionHandler = () => {
    if (typeof window === 'undefined') return;
    
    // Patch composition handler
    patchCompositionHandler();
    
    // Also patch any existing composition handlers
    setTimeout(() => {
        patchCompositionHandler();
    }, 1000);
    
    console.log('Safe composition handler initialized');
};
